/**
 * Carte de départ : « Val Cendrée », une vallée isolée avec une station.
 *
 * La carte décrit le relief (graine + éléments remarquables) et l'état
 * initial de la station. Les pistes initiales sont générées par le même
 * algorithme que celles construites en jeu.
 */
import type { BuildingKind, LiftKind } from './catalog';
import type { Vec2 } from './geometry';
import type { PisteGenOptions } from './pistes';
import type { MapFeatures } from './terrain';
import { TROIS_COMBES, VALLEE_AROLLES } from './massifs';

export interface MapLift {
  key: string;
  kind: LiftKind;
  name: string;
  bottom: Vec2;
  top: Vec2;
  color: number;
}

export interface MapPiste {
  name: string;
  /** Clé de la remontée desservant le départ. */
  fromLift: string;
  /** Arrivée : bas d'une remontée, ou "village". */
  toLift: string | 'village';
  gen: PisteGenOptions;
}

export interface MapBuilding {
  kind: BuildingKind;
  x: number;
  z: number;
  rot: number;
  /** Pour les enneigeurs : nom de la piste équipée. */
  piste?: string;
}

export interface GameMap {
  id: string;
  name: string;
  stationName: string;
  /** Présentation courte, affichée dans le choix de carte. */
  blurb: string;
  /** Orientation des versants skiables (ex. « Sud », « Nord-Est »). */
  faces: string[];
  /** Cadrage de départ de la caméra : point visé et cap ; déduit du village s'il manque. */
  view?: { x: number; z: number; yaw: number };
  features: MapFeatures;
  /** Altitude au-delà de laquelle il n'y a plus d'arbres (m). */
  treeLine: number;
  /** Rayon de la zone constructible du village (m). */
  villageZone: number;
  lifts: MapLift[];
  pistes: MapPiste[];
  buildings: MapBuilding[];
}

export const VAL_CENDREE: GameMap = {
  id: 'val-cendree',
  name: 'Val Cendrée',
  stationName: 'Chalet du Sommet',
  blurb: 'Une vallée tranquille et une seule face au sud : la carte pour débuter.',
  faces: ['Sud'],
  view: { x: 120, z: 330, yaw: 0.42 },
  features: {
    seed: 1207,
    village: { x: 110, z: 600, radius: 150 },
    lake: { x: -400, z: 660, radius: 100 },
  },
  treeLine: 360,
  villageZone: 330,
  lifts: [
    { key: 'sapiniere', kind: 'teleski', name: 'Téléski de la Sapinière', bottom: [250, 400], top: [285, 150], color: 2 },
    { key: 'cretes', kind: 'telesiege', name: 'Télésiège des Crêtes', bottom: [40, 420], top: [20, -600], color: 0 },
  ],
  pistes: [
    { name: 'Biche', fromLift: 'sapiniere', toLift: 'sapiniere', gen: { side: 1, grade: 0.12, seed: 1 } },
    { name: 'Marmotte', fromLift: 'cretes', toLift: 'cretes', gen: { side: 1, grade: 0.21, seed: 2 } },
    { name: 'Aiglon', fromLift: 'cretes', toLift: 'cretes', gen: { side: -1, grade: 0.31, seed: 3 } },
  ],
  buildings: [
    { kind: 'chalet_accueil', x: 120, z: 545, rot: 0 },
    { kind: 'ticket_office', x: 62, z: 585, rot: 0.35 },
    { kind: 'welcome_area', x: 140, z: 690, rot: 0 },
    { kind: 'shop_rental', x: 185, z: 590, rot: -0.3 },
    { kind: 'lamp_post', x: 92, z: 620, rot: 0 },
    { kind: 'lamp_post', x: 150, z: 625, rot: 0 },
    { kind: 'lamp_post', x: 92, z: 660, rot: 0 },
    { kind: 'lamp_post', x: 175, z: 650, rot: 0 },
    { kind: 'lamp_post', x: 70, z: 520, rot: 0 },
    { kind: 'lamp_post', x: 175, z: 520, rot: 0 },
    // Garage à dameuses du front de neige, entre les deux remontées, portes tournées vers les pistes.
    { kind: 'groomer_garage', x: 130, z: 410, rot: 3.1416 },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
  ],
};

/** Cartes proposées à la création d'une partie, dans l'ordre d'affichage. */
export const MAP_LIST: readonly GameMap[] = [VAL_CENDREE, TROIS_COMBES, VALLEE_AROLLES];

export const MAPS: Record<string, GameMap> = Object.fromEntries(MAP_LIST.map((m) => [m.id, m]));

/** Carte d'un monde, Val Cendrée si l'identifiant est inconnu. */
export function mapById(id: string | undefined): GameMap {
  return (id !== undefined && Object.hasOwn(MAPS, id) ? MAPS[id] : undefined) ?? VAL_CENDREE;
}

/** Noms de pistes disponibles (faune et flore alpines). */
export const PISTE_NAMES = [
  'Biche', 'Marmotte', 'Aiglon', 'Diamant', 'Chamois', 'Bouquetin', 'Lagopède', 'Hermine', 'Gypaète',
  'Edelweiss', 'Myrtille', 'Mélèze', 'Gentiane', 'Arnica', 'Lièvre blanc', 'Tétras', 'Renard', 'Choucas',
  'Grand Duc', 'Cristal', 'Névé', 'Sérac', 'Corniche', 'Arolle',
];

/** Compléments de noms de remontées. */
export const LIFT_NAMES = [
  'du Sommet', 'du Lac', 'des Chamois', 'du Col', "de l'Aiguille", 'des Marmottes', 'du Grand Pré',
  'de la Combe', 'des Sources', 'du Belvédère', 'des Arolles', 'du Glacier', 'de la Crête Blanche',
  'du Plan', 'des Lanches',
];
